// Kampanyalar / Meta kırılımları / Hesap Sağlığı sayfalarının ortak parçaları:
// biçimlendirme, durum rozetleri, sıralanabilir tablo, kart çerçevesi, segment seçici.
import React, { useMemo, useState } from 'react';
import { T, MONO } from '../poyais-ui/theme';
import { useLang, addTranslations } from '../i18n';
import { useIsMobile } from '../poyais-ui/hooks';

addTranslations({
  'Aktif': 'Active', 'Duraklatıldı': 'Paused', 'Kaldırıldı': 'Removed', 'Yayında': 'Eligible', 'Yayında değil': 'Not eligible', 'Sınırlı': 'Limited',
  'İncelemede': 'Under review', 'Eksik kurulum': 'Misconfigured', 'Bitti': 'Ended', 'Beklemede': 'Pending',
  'Mükemmel': 'Excellent', 'İyi': 'Good', 'Ortalama': 'Average', 'Zayıf': 'Poor', 'Hesaplanıyor': 'Pending', 'Reklam yok': 'No ads',
  'Onaylandı': 'Approved', 'Sınırlı onay': 'Approved (limited)', 'Reddedildi': 'Disapproved',
  'Tümünü Göster': 'Show all', 'Daha Az Göster': 'Show less', 'Bu dönem için veri yok.': 'No data for this period.',
  'Yükleniyor…': 'Loading…', 'Ara…': 'Search…',
});

export const CUR_SYMBOL: Record<string, string> = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };

export const money = (v: number | null | undefined, cur = 'TRY', short = false) => {
  if (v === null || v === undefined || isNaN(v as number)) return '—';
  const sym = CUR_SYMBOL[cur] ?? `${cur} `;
  const n = Number(v);
  if (short && Math.abs(n) >= 1000) {
    const k = Math.abs(n) >= 1e6 ? `${(n / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M` : `${(n / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: n >= 1e4 ? 0 : 1 })}K`;
    return sym + k;
  }
  if (short) return sym + n.toLocaleString('tr-TR', { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 2 });
  return sym + n.toLocaleString('tr-TR', { minimumFractionDigits: n >= 1000 ? 0 : 2, maximumFractionDigits: n >= 1000 ? 0 : 2 });
};
export const num = (v: number | null | undefined, digits = 0) =>
  v === null || v === undefined || isNaN(v as number) ? '—' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
export const pct = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined || isNaN(v as number) ? '—' : `%${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: digits })}`;
export const roasTxt = (v: number | null | undefined) =>
  v === null || v === undefined || !isFinite(v as number) || v === 0 ? '—' : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
export const roasColor = (v: number | null | undefined) => (!v ? T.textDim : v >= 3 ? T.greenText : v >= 1.5 ? T.amberText : T.redText);

// ── Durum / güç rozetleri ────────────────────────────────────────────────────
type Tone = 'green' | 'amber' | 'red' | 'gray' | 'blue';
const TONE: Record<Tone, [string, string]> = {
  green: ['var(--py-green-tint)', T.greenText], amber: ['var(--py-amber-tint)', T.amberText], red: ['var(--py-red-tint)', T.redText],
  gray: ['var(--py-track-bg)', T.textMidAlt], blue: ['var(--py-blue-tint)', T.blueText],
};
export function Pill({ children, tone = 'gray' }: { children: React.ReactNode; tone?: Tone }) {
  const [bg, fg] = TONE[tone];
  return <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700, color: fg, background: bg, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>{children}</span>;
}
const STATUS: Record<string, [string, Tone]> = {
  ENABLED: ['Aktif', 'green'], ACTIVE: ['Aktif', 'green'], PAUSED: ['Duraklatıldı', 'gray'], CAMPAIGN_PAUSED: ['Duraklatıldı', 'gray'], ADSET_PAUSED: ['Duraklatıldı', 'gray'],
  ARCHIVED: ['Kaldırıldı', 'gray'], REMOVED: ['Kaldırıldı', 'gray'], ELIGIBLE: ['Yayında', 'green'], NOT_ELIGIBLE: ['Yayında değil', 'red'], LIMITED: ['Sınırlı', 'amber'],
  PENDING: ['Beklemede', 'blue'], PENDING_REVIEW: ['İncelemede', 'blue'], IN_PROCESS: ['İncelemede', 'blue'], MISCONFIGURED: ['Eksik kurulum', 'red'], ENDED: ['Bitti', 'gray'],
  WITH_ISSUES: ['Sınırlı', 'amber'], DISAPPROVED: ['Reddedildi', 'red'],
};
export function StatusPill({ s }: { s?: string }) {
  const { t } = useLang();
  const [label, tone] = STATUS[(s || '').toUpperCase()] || [s || '—', 'gray' as Tone];
  return <Pill tone={tone}>{t(label)}</Pill>;
}
const STRENGTH: Record<string, [string, Tone]> = {
  EXCELLENT: ['Mükemmel', 'green'], GOOD: ['İyi', 'green'], AVERAGE: ['Ortalama', 'amber'], POOR: ['Zayıf', 'red'], PENDING: ['Hesaplanıyor', 'gray'], NO_ADS: ['Reklam yok', 'gray'],
};
export function StrengthPill({ s }: { s?: string }) {
  const { t } = useLang();
  const v = STRENGTH[(s || '').toUpperCase()];
  return v ? <Pill tone={v[1]}>{t(v[0])}</Pill> : <span style={{ color: T.textDim }}>—</span>;
}
const APPROVAL: Record<string, [string, Tone]> = {
  APPROVED: ['Onaylandı', 'green'], APPROVED_LIMITED: ['Sınırlı onay', 'amber'], AREA_OF_INTEREST_ONLY: ['Sınırlı onay', 'amber'], DISAPPROVED: ['Reddedildi', 'red'],
};
export function ApprovalPill({ s }: { s?: string }) {
  const { t } = useLang();
  const v = APPROVAL[(s || '').toUpperCase()];
  return v ? <Pill tone={v[1]}>{t(v[0])}</Pill> : <span style={{ color: T.textDim }}>—</span>;
}

// ── Kart ─────────────────────────────────────────────────────────────────────
export function Panel({ title, sub, right, children, pad = true }: { title?: React.ReactNode; sub?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; pad?: boolean }) {
  return (
    <section style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
      {(title || right) && (
        <header style={{ padding: '16px 20px', borderBottom: `1px solid ${T.tableDivider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            {title && <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{title}</div>}
            {sub && <div style={{ fontSize: 12, color: T.textDim, marginTop: 3, lineHeight: 1.5 }}>{sub}</div>}
          </div>
          {right && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{right}</div>}
        </header>
      )}
      <div style={pad ? { padding: '16px 20px' } : undefined}>{children}</div>
    </section>
  );
}

export function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.55 }}>{children}</div>;
}

export function Loading() {
  const { t } = useLang();
  return <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: T.textDim }}>{t('Yükleniyor…')}</div>;
}

// ── Segment seçici (yatay kaydırılabilir çipler) ─────────────────────────────
export function Segments({ items, value, onChange }: { items: { id: string; label: string; count?: number }[]; value: string; onChange: (id: string) => void }) {
  return (
    <div role="tablist" style={{ display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any, paddingBottom: 2, scrollbarWidth: 'none' as any }}>
      {items.map(it => {
        const on = it.id === value;
        return (
          <button key={it.id} role="tab" aria-selected={on} onClick={() => onChange(it.id)}
            style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 999, border: `1px solid ${on ? T.accent : T.borderMid}`, background: on ? 'var(--py-amber-tint)' : T.surface,
              color: on ? 'var(--py-amber-deep-text)' : T.textMidAlt, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            {it.label}{it.count !== undefined && <span style={{ marginLeft: 6, fontWeight: 600, opacity: 0.7 }}>{it.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Sıralanabilir tablo ──────────────────────────────────────────────────────
export type Col<R> = {
  key: string; label: string; align?: 'left' | 'right'; width?: number | string; mono?: boolean;
  value?: (r: R) => number | string | null | undefined;   // sıralama için
  render?: (r: R) => React.ReactNode;
};
const titleCase = (s: string) => s.split(' ').map(w => /^[A-ZÇĞİÖŞÜ0-9]+$/.test(w) ? w : w.charAt(0).toLocaleUpperCase('tr') + w.slice(1)).join(' ');

export function DataTable({ rows, cols, initialSort, initialDir = -1, pageSize = 12, minWidth = 640, empty, mobileCards = true }: { rows: any[]; cols: Col<any>[]; initialSort?: string; initialDir?: 1 | -1; pageSize?: number; minWidth?: number; empty?: string; mobileCards?: boolean }) {
  const { t } = useLang();
  const [sort, setSort] = useState<{ k: string; dir: 1 | -1 }>({ k: initialSort || '', dir: initialDir });
  const [page, setPage] = useState(0);
  const sorted = useMemo(() => {
    const c = cols.find(x => x.key === sort.k);
    if (!c) return rows;
    const get = c.value || ((r: any) => r[c.key]);
    return [...rows].sort((a, b) => {
      const av = get(a), bv = get(b);
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return (typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), 'tr')) * sort.dir;
    });
  }, [rows, cols, sort]);
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const cur = Math.min(page, pages - 1);
  const shown = sorted.slice(cur * pageSize, (cur + 1) * pageSize);
  const mobile = useIsMobile() && mobileCards;
  if (!rows.length) return <div style={{ padding: '22px 20px', fontSize: 13, color: T.textDim }}>{empty || t('Bu dönem için veri yok.')}</div>;
  return (
    <>
      {mobile && (
        <div>
          <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderBottom: `1px solid ${T.tableDivider}`, alignItems: 'center' }}>
            <span style={{ fontSize: 11.5, color: T.textDim, whiteSpace: 'nowrap' }}>{t('Sırala')}</span>
            <select value={sort.k} onChange={e => { setPage(0); setSort({ k: e.target.value, dir: -1 }); }} aria-label={t('Sırala')}
              style={{ flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 9, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.text, fontSize: 12.5, fontFamily: 'inherit' }}>
              {cols.slice(1).map(c => <option key={c.key} value={c.key}>{titleCase(c.label)}</option>)}
            </select>
            <button onClick={() => setSort(x => ({ ...x, dir: x.dir === 1 ? -1 : 1 }))} aria-label="↕" style={{ width: 34, height: 34, borderRadius: 9, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.textMidAlt, fontSize: 14, cursor: 'pointer' }}>{sort.dir === -1 ? '↓' : '↑'}</button>
          </div>
          {shown.map((r, i) => (
            <div key={i} style={{ padding: '14px', borderBottom: `1px solid ${T.tableDivider}` }}>
              <div style={{ fontSize: 13 }}>{cols[0].render ? cols[0].render(r) : String((r as any)[cols[0].key] ?? '—')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 14px', marginTop: 10 }}>
                {cols.slice(1).map(c => (
                  <div key={c.key} style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: T.textDim, marginBottom: 2 }}>{titleCase(c.label)}</div>
                    <div style={{ fontSize: 12.5, color: T.text, fontFamily: c.mono ? MONO : undefined, overflowWrap: 'anywhere' }}>{c.render ? c.render(r) : String((r as any)[c.key] ?? '—')}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ overflowX: 'auto', display: mobile ? 'none' : undefined, WebkitOverflowScrolling: 'touch' as any }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth }}>
          <thead style={{ background: T.subtleBg }}>
            <tr>{cols.map(c => {
              const on = sort.k === c.key;
              return (
                <th key={c.key} onClick={() => { setPage(0); setSort(s => ({ k: c.key, dir: s.k === c.key ? (s.dir === 1 ? -1 : 1) : -1 })); }}
                  style={{ padding: '10px 14px', fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: on ? 'var(--py-amber-deep-text)' : T.textMidAlt,
                    textAlign: c.align || 'left', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', borderBottom: `1px solid ${T.tableDivider}`, width: c.width }}>
                  {c.label}{on ? (sort.dir === -1 ? ' ↓' : ' ↑') : ''}
                </th>
              );
            })}</tr>
          </thead>
          <tbody>{shown.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${T.tableDivider}` }}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '11px 14px', fontSize: 12.5, color: T.text, textAlign: c.align || 'left', fontFamily: c.mono ? MONO : undefined, verticalAlign: 'middle', whiteSpace: c.align === 'right' ? 'nowrap' : undefined }}>
                  {c.render ? c.render(r) : String((r as any)[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </div>
      {rows.length > pageSize && (
        <div style={{ padding: '10px 20px', borderTop: `1px solid ${T.tableDivider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: T.textDim }}>{cur * pageSize + 1}–{Math.min((cur + 1) * pageSize, rows.length)} / {rows.length}</span>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {(() => {
              const nums: (number | '…')[] = [];
              for (let i = 0; i < pages; i++) if (i === 0 || i === pages - 1 || Math.abs(i - cur) <= 1) nums.push(i); else if (nums[nums.length - 1] !== '…') nums.push('…');
              const btn: React.CSSProperties = { minWidth: 30, height: 30, borderRadius: 8, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.textMidAlt, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '0 6px' };
              return <>
                <button aria-label="‹" disabled={cur === 0} onClick={() => setPage(cur - 1)} style={{ ...btn, opacity: cur === 0 ? 0.4 : 1, cursor: cur === 0 ? 'not-allowed' : 'pointer' }}>‹</button>
                {nums.map((n, i) => n === '…' ? <span key={'e' + i} style={{ color: T.textDim, padding: '0 4px' }}>…</span>
                  : <button key={n} onClick={() => setPage(n)} aria-current={n === cur} style={{ ...btn, background: n === cur ? T.accent : T.surface, color: n === cur ? 'var(--py-accent-ink)' : T.textMidAlt, borderColor: n === cur ? T.accent : T.borderMid }}>{n + 1}</button>)}
                <button aria-label="›" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)} style={{ ...btn, opacity: cur >= pages - 1 ? 0.4 : 1, cursor: cur >= pages - 1 ? 'not-allowed' : 'pointer' }}>›</button>
              </>;
            })()}
          </div>
        </div>
      )}
    </>
  );
}

// İsim + alt satır (ör. kampanya adı altında tür) hücresi
export function NameCell({ name, sub }: { name: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0, maxWidth: 360 }}>
      <div style={{ fontWeight: 700, color: T.text, lineHeight: 1.35, overflowWrap: 'anywhere' }}>{name}</div>
      {sub && <div style={{ fontSize: 11.5, color: T.textDim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Harcama + pay çubuğu hücresi
export function ShareCell({ text, share }: { text: string; share: number }) {
  const mobile = useIsMobile();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: mobile ? 'flex-start' : 'flex-end', flexDirection: mobile ? 'row-reverse' : 'row' }}>
      <div style={{ width: 56, height: 5, background: T.trackBg, borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${Math.max(0, Math.min(100, share))}%`, height: '100%', background: T.accent }} /></div>
      <span style={{ fontFamily: MONO, minWidth: 64, textAlign: 'right' }}>{text}</span>
    </div>
  );
}

export function useMobile() { return useIsMobile(); }
